import { useMutation, useQuery } from "@tanstack/react-query";
import { apiService } from "@/services";
import { ApiError } from "@/services/api.service";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import type {
  SeoStrategyEditBody,
  SeoStrategyRecommendation,
  SeoStrategyRecommendationsResponse,
} from "@/types/seo-strategy";

// AUT-430 (WEB-013, `components/ab-test.md § A test write refreshes the strategy list`): the
// prefix the four A/B test-writing hooks in `resources/product/product.api.ts` invalidate. They
// hold no ASIN, only a landscape-product id, so they cannot build the exact key below -- they
// invalidate every mounted strategy list instead. Same pattern as `keywordApi.getKeywordsQueryPrefix`.
//
// AUT-196: `strategy-run.api.ts` is the second such consumer -- it invalidates this list on the
// run's in-flight -> ended transition and likewise holds only the product id, not the ASIN.
export const getSeoStrategyRecommendationsQueryPrefix = () =>
  ["seo-strategy-recommendations"] as const;

// AUT-223: recommendations are identified by (organization, ASIN) server-side, so the
// ASIN is the only key the client needs.
// AUT-223: recommendations are identified by (organization, ASIN) server-side, so the
// ASIN is the only key the client needs.
export const getSeoStrategyRecommendationsQueryKey = (asin: string) =>
  [...getSeoStrategyRecommendationsQueryPrefix(), "list", asin] as const;

export const useGetSeoStrategyRecommendations = (asin: string | undefined) =>
  useQuery<SeoStrategyRecommendationsResponse>({
    queryKey: getSeoStrategyRecommendationsQueryKey(asin ?? ""),
    queryFn: () =>
      apiService.get<SeoStrategyRecommendationsResponse, { asin: string }>(
        "/seo-strategy/recommendations",
        { asin: asin ?? "" },
      ),
    enabled: Boolean(asin),
    staleTime: DEFAULT_STALE_TIME,
  });

/**
 * Transitions refetch rather than patching the cache optimistically: prioritizing or
 * dismissing frees a slot in the 5-cap, which can backfill a different recommendation
 * server-side, so the list after the call is not something the client can predict.
 *
 * AUT-295: the promise is RETURNED, not fired and forgotten. react-query awaits a
 * promise returned from onSettled before a mutation reports settled, so isPending stays
 * true until the list has actually refetched. Without that, the buttons re-enabled the
 * moment the POST resolved while the just-actioned card was still on screen for another
 * second or two -- long enough to click it twice.
 */
const invalidate = (asin: string) =>
  queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryKey(asin) });

function bucketFor(status: SeoStrategyRecommendation["status"]): keyof SeoStrategyRecommendationsResponse {
  if (status === "prioritized") return "prioritized";
  if (status === "completed" || status === "resolved") return "completed";
  if (status === "dismissed") return "dismissed";
  return "pending";
}

/**
 * AUT-430 (`data-flow.md § Flow: strategy slot refill`, hop 6): "moves on success, then
 * invalidate" -- a transition's response is the moved item itself, written into whichever
 * list its new status belongs to before the list is invalidated, because the refetch that
 * follows will not yet carry the backfill (generation is a queued job).
 */
function moveInCache(asin: string, updated: SeoStrategyRecommendation) {
  queryClient.setQueryData<SeoStrategyRecommendationsResponse>(
    getSeoStrategyRecommendationsQueryKey(asin),
    (previous) => {
      if (!previous) return previous;

      const withoutMoved = {
        ...previous,
        pending: previous.pending.filter((recommendation) => recommendation.id !== updated.id),
        prioritized: previous.prioritized.filter((recommendation) => recommendation.id !== updated.id),
        completed: previous.completed.filter((recommendation) => recommendation.id !== updated.id),
        dismissed: previous.dismissed.filter((recommendation) => recommendation.id !== updated.id),
      };

      const bucket = bucketFor(updated.status);
      return { ...withoutMoved, [bucket]: [updated, ...withoutMoved[bucket]] };
    },
  );
}

/**
 * WEB-002 (`components/seo-strategy.md § The screen`, the `200` row): an edit never
 * changes status, so the item stays wherever it already is instead of moving between
 * lists. Exactly eight keys are merged in from the response -- never the whole item, and
 * never the response's `thematicGrouping` object wholesale: that response carries the
 * *stored* grouping (`tests: []`, snapshot keyword metrics), not the live one the cache
 * holds, so only its `name` is taken. A cached `null` grouping stays `null`.
 */
function mergeEditInCache(asin: string, updated: SeoStrategyRecommendation) {
  queryClient.setQueryData<SeoStrategyRecommendationsResponse>(
    getSeoStrategyRecommendationsQueryKey(asin),
    (previous) => {
      if (!previous) return previous;

      const mergeOne = (recommendation: SeoStrategyRecommendation): SeoStrategyRecommendation => {
        if (recommendation.id !== updated.id) return recommendation;

        const grouping = recommendation.thematicGrouping;

        return {
          ...recommendation,
          gapTitle: updated.gapTitle,
          questionText: updated.questionText,
          consolidatedFinding: updated.consolidatedFinding,
          whyItMatters: updated.whyItMatters,
          evidenceSummary: updated.evidenceSummary,
          field: updated.field,
          hypothesis: updated.hypothesis,
          thematicGrouping: grouping === null ? null : { ...grouping, name: updated.thematicGrouping?.name ?? grouping.name },
        };
      };

      return {
        ...previous,
        pending: previous.pending.map(mergeOne),
        prioritized: previous.prioritized.map(mergeOne),
        completed: previous.completed.map(mergeOne),
        dismissed: previous.dismissed.map(mergeOne),
      };
    },
  );
}

/**
 * WEB-007: "landing" -- the flight's `onLand`, or the immediate commit under
 * reduced-motion / no WAAPI -- is when the optimistic move happens, not the request's
 * settle. The caller (`StrategyBrowser`) decides when that is and calls this directly.
 * Idempotent: if the item has already left `pending` (a refetch beat the flight, or a
 * stray second call for the same id), this writes nothing, so the item is never
 * duplicated into `prioritized` and the counts do not move a second time.
 */
export function commitPrioritizedInCache(asin: string, recommendation: SeoStrategyRecommendation): void {
  queryClient.setQueryData<SeoStrategyRecommendationsResponse>(
    getSeoStrategyRecommendationsQueryKey(asin),
    (previous) => {
      if (!previous) return previous;
      if (!previous.pending.some((item) => item.id === recommendation.id)) return previous;

      return {
        ...previous,
        pending: previous.pending.filter((item) => item.id !== recommendation.id),
        prioritized: [{ ...recommendation, status: "prioritized" }, ...previous.prioritized],
      };
    },
  );
}

/**
 * WEB-007: exported so both the hook's failure path and the caller's deferred success
 * path (fired once landed and succeeded are both true, whichever happens second) can
 * invalidate through the same one place.
 */
export function invalidateSeoStrategyRecommendations(asin: string): Promise<void> {
  return invalidate(asin);
}

type PrioritizeMutationContext = { previous: SeoStrategyRecommendationsResponse | undefined };

/**
 * The optimistic move, the flight and the timed toast belong to the caller (`WEB-007`) --
 * this hook only carries the typed response, snapshots the cache at click (so the
 * caller's optimistic move at landing and this hook's own rollback both start from the
 * same point), and rolls back + invalidates immediately on failure.
 *
 * Success does NOT invalidate here: on a fast request that would fire before landing, and
 * the refetch would pull the card out of `pending` mid-flight. The caller invalidates
 * once landing and success are both true, via `invalidateSeoStrategyRecommendations`.
 */
export const usePrioritizeSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, { id: number; asin: string }, PrioritizeMutationContext>({
    mutationFn: ({ id }) =>
      apiService.post<SeoStrategyRecommendation, Record<string, never>>(
        `/seo-strategy/recommendations/${id}/prioritize`,
        {},
      ),
    onMutate: async (variables) => {
      const queryKey = getSeoStrategyRecommendationsQueryKey(variables.asin);
      await queryClient.cancelQueries({ queryKey });
      return { previous: queryClient.getQueryData<SeoStrategyRecommendationsResponse>(queryKey) };
    },
    onError: (error, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(getSeoStrategyRecommendationsQueryKey(variables.asin), context.previous);
      }
      handleApiError(error);
      invalidateSeoStrategyRecommendations(variables.asin);
    },
  });

/** AUT-430 FR-11: a `pending` recommendation dismissed with a reason. */
export const useDismissSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, { id: number; asin: string; reason: string; notes?: string }>({
    mutationFn: ({ id, reason, notes }) =>
      apiService.post<SeoStrategyRecommendation, { reason: string; notes?: string }>(
        `/seo-strategy/recommendations/${id}/dismiss`,
        { reason, notes },
      ),
    onSuccess: (data, variables) => moveInCache(variables.asin, data),
    onError: (error) => handleApiError(error),
    onSettled: (_data, _error, variables) => invalidate(variables.asin),
  });

/**
 * `decisions/removing-a-prioritized-strategy-is-a-dismissal.md`: `remove` is a dismissal
 * reached from a `prioritized` card -- same body as `dismiss`, ending in `dismissed`.
 */
export const useRemoveSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, { id: number; asin: string; reason: string; notes?: string }>({
    mutationFn: ({ id, reason, notes }) =>
      apiService.post<SeoStrategyRecommendation, { reason: string; notes?: string }>(
        `/seo-strategy/recommendations/${id}/remove`,
        { reason, notes },
      ),
    onSuccess: (data, variables) => moveInCache(variables.asin, data),
    onError: (error) => handleApiError(error),
    onSettled: (_data, _error, variables) => invalidate(variables.asin),
  });

/** AUT-430 FR-15: a prioritized strategy finished by hand, no dialog, no linked test. */
export const useCompleteSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, { id: number; asin: string }>({
    mutationFn: ({ id }) =>
      apiService.post<SeoStrategyRecommendation, Record<string, never>>(
        `/seo-strategy/recommendations/${id}/complete`,
        {},
      ),
    onSuccess: (data, variables) => moveInCache(variables.asin, data),
    onError: (error) => handleApiError(error),
    onSettled: (_data, _error, variables) => invalidate(variables.asin),
  });

/**
 * WEB-006 (AUT-430 spec B, `components/seo-strategy.md § Restore`): the one transition
 * out of `dismissed`. No body. Like `dismiss` / `remove` / `complete`, a successful
 * response moves the item into its new bucket (`pending` -- the response's `status`) via
 * the shared `moveInCache`; the caller (`StrategyBrowser`) owns the toast and the
 * segment/filter switch to `Other` → `Recommended`. Every failure -- the FR-15 `409`,
 * "no longer dismissed" `409`, network or `5xx` -- calls `handleApiError` and
 * invalidates so the lists refresh to where the strategy actually is.
 */
export const useRestoreSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, { id: number; asin: string }>({
    mutationFn: ({ id }) =>
      apiService.post<SeoStrategyRecommendation, Record<string, never>>(
        `/seo-strategy/recommendations/${id}/restore`,
        {},
      ),
    onSuccess: (data, variables) => moveInCache(variables.asin, data),
    onError: (error) => handleApiError(error),
    onSettled: (_data, _error, variables) => invalidate(variables.asin),
  });

/** Section 2: the explicit action that adds a suggested grouping to the Master List. */
export const useAddGroupingToMasterList = () =>
  useMutation<unknown, unknown, { id: number; asin: string }>({
    mutationFn: ({ id }) =>
      apiService.post(`/seo-strategy/recommendations/${id}/add-grouping-to-master-list`, {}),
    onError: (error) => handleApiError(error),
    onSettled: (_data, _error, variables) => invalidate(variables.asin),
  });

type UpdateSeoStrategyRecommendationVariables = { id: number; asin: string } & SeoStrategyEditBody;

/**
 * FEATURE-003 (AUT-430 spec B, `components/seo-strategy.md § Edit`): the whole-form save
 * that overwrites a prioritized strategy's generated text. Unlike every other transition
 * in this module, a successful edit never moves the item between lists -- it merges eight
 * keys in place (`mergeEditInCache`) and invalidates.
 *
 * `409` (the strategy is no longer `prioritized`, e.g. removed or completed elsewhere)
 * still invalidates: the failure table's "the lists refresh to where the strategy is
 * now" (`StrategyBrowser` awaits `mutateAsync` per card and closes the form on this status
 * alone, and no other). `422`, a network error or a `5xx` leave the cache untouched and do not
 * invalidate -- the caller's draft is still good to retry against.
 *
 * The error is never swallowed here: `useMutation` rejects on every failure path, so the
 * caller's own `onError` / `isError` / awaited `mutateAsync` still see it after this
 * hook's own `onError` has run.
 */
export const useUpdateSeoStrategyRecommendation = () =>
  useMutation<SeoStrategyRecommendation, unknown, UpdateSeoStrategyRecommendationVariables>({
    mutationFn: ({ id, asin: _asin, ...body }) =>
      apiService.patch<SeoStrategyRecommendation, SeoStrategyEditBody>(
        `/seo-strategy/recommendations/${id}`,
        body,
      ),
    onSuccess: (data, variables) => {
      mergeEditInCache(variables.asin, data);
      invalidate(variables.asin);
    },
    onError: (error, variables) => {
      handleApiError(error);
      // 409 (no longer `prioritized`) and 422 (`renamedGrouping` -- the stored grouping is
      // no longer `suggested`) both mean the cached item disagrees with the server about
      // something this save depended on; invalidate so the cache -- and the grouping
      // control's editability -- catches up, instead of every retry repeating the same
      // rejection against a fact that already changed server-side.
      if (error instanceof ApiError && (error.status === 409 || error.status === 422)) {
        invalidate(variables.asin);
      }
    },
  });
